/**
 * lib/incomingInspectionSpc.js
 * Client-side SPC math + canvas control-chart rendering for the
 * Incoming/In-Process Inspection module. Ported 1:1 from the standalone
 * tool's assets/js/spc-chart.js so the live preview here matches exactly
 * what the backend (backend/incoming_inspection.py::spc_compute) computes
 * and stores.
 */

export const SPC_CONSTANTS = {
  2: { A2: 1.880, D3: 0.000, D4: 3.267, d2: 1.128 },
  3: { A2: 1.023, D3: 0.000, D4: 2.574, d2: 1.693 },
  4: { A2: 0.729, D3: 0.000, D4: 2.282, d2: 2.059 },
  5: { A2: 0.577, D3: 0.000, D4: 2.114, d2: 2.326 },
  6: { A2: 0.483, D3: 0.000, D4: 2.004, d2: 2.534 },
  7: { A2: 0.419, D3: 0.076, D4: 1.924, d2: 2.704 },
  8: { A2: 0.373, D3: 0.136, D4: 1.864, d2: 2.847 },
  9: { A2: 0.337, D3: 0.184, D4: 1.816, d2: 2.970 },
  10: { A2: 0.308, D3: 0.223, D4: 1.777, d2: 3.078 },
};

export function computeSPC(subgroups, usl, lsl) {
  const n = Math.max(2, Math.min(10, subgroups[0]?.length || 2));
  const c = SPC_CONSTANTS[n];

  const means = subgroups.map((sg) => sg.reduce((a, b) => a + b, 0) / sg.length);
  const ranges = subgroups.map((sg) => Math.max(...sg) - Math.min(...sg));

  const xDoubleBar = means.reduce((a, b) => a + b, 0) / means.length;
  const rBar = ranges.reduce((a, b) => a + b, 0) / ranges.length;

  const uclX = xDoubleBar + c.A2 * rBar;
  const lclX = xDoubleBar - c.A2 * rBar;
  const uclR = c.D4 * rBar;
  const lclR = c.D3 * rBar;

  const flags = means.map((m, i) => m > uclX || m < lclX || ranges[i] > uclR || ranges[i] < lclR);
  const outOfControlCount = flags.filter(Boolean).length;

  let cp = null, cpk = null;
  if (usl != null && lsl != null && usl > lsl) {
    const sigma = rBar / c.d2;
    if (sigma > 0) {
      cp = (usl - lsl) / (6 * sigma);
      const cpu = (usl - xDoubleBar) / (3 * sigma);
      const cpl = (xDoubleBar - lsl) / (3 * sigma);
      cpk = Math.min(cpu, cpl);
    }
  }

  return { means, ranges, flags, xDoubleBar, rBar, uclX, lclX, uclR, lclR, cp, cpk, outOfControlCount, n };
}

/**
 * Draws a control chart onto a canvas element.
 * opts: { points, ucl, lcl, cl, flags }
 */
export function drawControlChart(canvas, opts) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);

  const padL = 60, padR = 30, padT = 20, padB = 40;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const { points, ucl, lcl, cl, flags = [] } = opts;
  if (!points || points.length === 0) return;
  const allVals = [...points, ucl, lcl, cl];
  let minV = Math.min(...allVals), maxV = Math.max(...allVals);
  const span = (maxV - minV) || 1;
  minV -= span * 0.15; maxV += span * 0.15;

  const xFor = (i) => padL + (points.length <= 1 ? chartW / 2 : (i / (points.length - 1)) * chartW);
  const yFor = (v) => padT + chartH - ((v - minV) / (maxV - minV)) * chartH;

  ctx.strokeStyle = '#eef0f6';
  ctx.lineWidth = 1;
  for (let g = 0; g <= 4; g++) {
    const gy = padT + (g / 4) * chartH;
    ctx.beginPath(); ctx.moveTo(padL, gy); ctx.lineTo(W - padR, gy); ctx.stroke();
  }

  function hLine(v, color, dash, label) {
    const y = yFor(v);
    ctx.save();
    ctx.strokeStyle = color; ctx.setLineDash(dash); ctx.lineWidth = 1.4;
    ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = color; ctx.font = '600 11px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(label + ' ' + v.toFixed(3), padL + 4, y - 4);
    ctx.restore();
  }
  hLine(ucl, '#dc2626', [5, 4], 'UCL');
  hLine(cl, '#2f5fe0', [], 'CL');
  hLine(lcl, '#dc2626', [5, 4], 'LCL');

  ctx.fillStyle = '#5a6482'; ctx.font = '500 10.5px Inter, sans-serif'; ctx.textAlign = 'right';
  for (let g = 0; g <= 4; g++) {
    const v = maxV - (g / 4) * (maxV - minV);
    ctx.fillText(v.toFixed(2), padL - 8, padT + (g / 4) * chartH + 4);
  }

  ctx.strokeStyle = '#264ec2'; ctx.lineWidth = 2;
  ctx.beginPath();
  points.forEach((v, i) => {
    const x = xFor(i), y = yFor(v);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  });
  ctx.stroke();

  points.forEach((v, i) => {
    const x = xFor(i), y = yFor(v);
    const bad = flags[i];
    ctx.beginPath();
    ctx.arc(x, y, 4.2, 0, Math.PI * 2);
    ctx.fillStyle = bad ? '#dc2626' : '#2f5fe0';
    ctx.fill();
    ctx.lineWidth = 2; ctx.strokeStyle = '#fff'; ctx.stroke();

    ctx.fillStyle = '#8891ab'; ctx.font = '500 10px Inter, sans-serif'; ctx.textAlign = 'center';
    if (points.length <= 30 || i % Math.ceil(points.length / 30) === 0) {
      ctx.fillText(String(i + 1), x, H - padB + 16);
    }
  });

  ctx.strokeStyle = '#c7cde0'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, H - padB); ctx.lineTo(W - padR, H - padB); ctx.stroke();
}
